import * as $maths from "../gleam_community_maths/gleam_community/maths.mjs";
import * as $bool from "../gleam_stdlib/gleam/bool.mjs";
import * as $float from "../gleam_stdlib/gleam/float.mjs";
import * as $int from "../gleam_stdlib/gleam/int.mjs";
import * as $list from "../gleam_stdlib/gleam/list.mjs";
import * as $p5js_gleam from "../p5js_gleam/p5js_gleam.mjs";
import * as $p5 from "../p5js_gleam/p5js_gleam/bindings.mjs";
import * as $random from "../prng/prng/random.mjs";
import * as $behavior_tree from "./behavior_tree/behavior_tree.mjs";
import * as $bullet from "./bullet.mjs";
import * as $dungeon from "./dungeon.mjs";
import {
  Ok,
  toList,
  Empty as $Empty,
  CustomType as $CustomType,
  makeError,
  divideFloat,
  isEqual,
} from "./gleam.mjs";
import * as $obstacle from "./obstacle.mjs";
import * as $pit from "./pit.mjs";
import * as $player from "./player.mjs";
import * as $room from "./room.mjs";
import * as $utils from "./utils.mjs";
import * as $vector from "./vector.mjs";

const FILEPATH = "src/enemy.gleam";

/**
 * Represents the input data needed for an enemy to update.
 */
export class Inputs extends $CustomType {
  constructor(enemies, dungeon, player) {
    super();
    this.enemies = enemies;
    this.dungeon = dungeon;
    this.player = player;
  }
}
export const Inputs$Inputs = (enemies, dungeon, player) =>
  new Inputs(enemies, dungeon, player);
export const Inputs$isInputs = (value) => value instanceof Inputs;
export const Inputs$Inputs$enemies = (value) => value.enemies;
export const Inputs$Inputs$0 = (value) => value.enemies;
export const Inputs$Inputs$dungeon = (value) => value.dungeon;
export const Inputs$Inputs$1 = (value) => value.dungeon;
export const Inputs$Inputs$player = (value) => value.player;
export const Inputs$Inputs$2 = (value) => value.player;

/**
 * Represents the additional outputs from updating an enemy.
 */
export class AdditionalOutputs extends $CustomType {
  constructor(bullets) {
    super();
    this.bullets = bullets;
  }
}
export const AdditionalOutputs$AdditionalOutputs = (bullets) =>
  new AdditionalOutputs(bullets);
export const AdditionalOutputs$isAdditionalOutputs = (value) =>
  value instanceof AdditionalOutputs;
export const AdditionalOutputs$AdditionalOutputs$bullets = (value) =>
  value.bullets;
export const AdditionalOutputs$AdditionalOutputs$0 = (value) => value.bullets;

/**
 * Represents an enemy to defeat.
 */
export class Enemy extends $CustomType {
  constructor(position, rotation, velocity, rotational_velocity, current_health, max_health, path, last_path_updated, spotted_player, last_bullet_fired, btree) {
    super();
    this.position = position;
    this.rotation = rotation;
    this.velocity = velocity;
    this.rotational_velocity = rotational_velocity;
    this.current_health = current_health;
    this.max_health = max_health;
    this.path = path;
    this.last_path_updated = last_path_updated;
    this.spotted_player = spotted_player;
    this.last_bullet_fired = last_bullet_fired;
    this.btree = btree;
  }
}
export const Enemy$Enemy = (position, rotation, velocity, rotational_velocity, current_health, max_health, path, last_path_updated, spotted_player, last_bullet_fired, btree) =>
  new Enemy(position,
  rotation,
  velocity,
  rotational_velocity,
  current_health,
  max_health,
  path,
  last_path_updated,
  spotted_player,
  last_bullet_fired,
  btree);
export const Enemy$isEnemy = (value) => value instanceof Enemy;
export const Enemy$Enemy$position = (value) => value.position;
export const Enemy$Enemy$0 = (value) => value.position;
export const Enemy$Enemy$rotation = (value) => value.rotation;
export const Enemy$Enemy$1 = (value) => value.rotation;
export const Enemy$Enemy$velocity = (value) => value.velocity;
export const Enemy$Enemy$2 = (value) => value.velocity;
export const Enemy$Enemy$rotational_velocity = (value) =>
  value.rotational_velocity;
export const Enemy$Enemy$3 = (value) => value.rotational_velocity;
export const Enemy$Enemy$current_health = (value) => value.current_health;
export const Enemy$Enemy$4 = (value) => value.current_health;
export const Enemy$Enemy$max_health = (value) => value.max_health;
export const Enemy$Enemy$5 = (value) => value.max_health;
export const Enemy$Enemy$path = (value) => value.path;
export const Enemy$Enemy$6 = (value) => value.path;
export const Enemy$Enemy$last_path_updated = (value) => value.last_path_updated;
export const Enemy$Enemy$7 = (value) => value.last_path_updated;
export const Enemy$Enemy$spotted_player = (value) => value.spotted_player;
export const Enemy$Enemy$8 = (value) => value.spotted_player;
export const Enemy$Enemy$last_bullet_fired = (value) => value.last_bullet_fired;
export const Enemy$Enemy$9 = (value) => value.last_bullet_fired;
export const Enemy$Enemy$btree = (value) => value.btree;
export const Enemy$Enemy$10 = (value) => value.btree;

/**
 * The size of an enemy in pixels.
 */
export const enemy_size = 10.0;

/**
 * The amount of damage an enemy does.
 */
export const damage = 5;

/**
 * The score reward for killing an enemy.
 */
export const value = 100;

const max_enemy_health = 80;

const bullet_wait_time = 200;

const enemy_gravity_strength = 0.02;

const whisker_length = 20.0;

const target_radius = 10.0;

const slow_radius = 40.0;

const max_speed = 3.0;

const max_acceleration = 3.0;

const wall_avoid_force = max_acceleration;

const jump_power = 0.3;

const pit_avoid_force = 1000.0;

const pit_avoid_radius = 30.0;

const avoid_force = 200.0;

const avoid_radius = 50.0;

const cluster_force = 100.0;

const cluster_radius = 20.0;

const dead_fill_color = "#000000";

const enemy_fill_color = "#ff7b00";

const enemy_stroke_color = "#000000";

function random_direction(loop$position, loop$dungeon) {
  while (true) {
    let position = loop$position;
    let dungeon = loop$dungeon;
    let _block;
    let _pipe = $random.int(0, 8);
    _block = $random.random_sample(_pipe);
    let dir = _block;
    let _block$1;
    if (dir === 0) {
      _block$1 = new $room.Left();
    } else if (dir === 1) {
      _block$1 = new $room.Right();
    } else if (dir === 2) {
      _block$1 = new $room.Top();
    } else if (dir === 3) {
      _block$1 = new $room.Bottom();
    } else if (dir === 4) {
      _block$1 = new $room.TopLeft();
    } else if (dir === 5) {
      _block$1 = new $room.TopRight();
    } else if (dir === 6) {
      _block$1 = new $room.BottomLeft();
    } else {
      _block$1 = new $room.BottomRight();
    }
    let dir$1 = _block$1;
    let $ = $dungeon.point_to_coordinate(position);
    let col;
    let row;
    col = $[0];
    row = $[1];
    let _block$2;
    let _pipe$1 = $dungeon.next_room_indices(col, row, dir$1);
    _block$2 = $dungeon.coordinate_to_point(_pipe$1);
    let new_position = _block$2;
    let $1 = $dungeon.can_move(dungeon, position, new_position);
    if ($1) {
      return new_position;
    } else {
      loop$position = position;
      loop$dungeon = dungeon;
    }
  }
}

export function has_path_behavior(inputs) {
  let enemy;
  enemy = inputs.entity;
  return new $behavior_tree.BehaviorResult(
    !isEqual(enemy.path, toList([])),
    enemy,
    new AdditionalOutputs(toList([])),
  );
}

export function path_is_stale_behavior(inputs) {
  let enemy;
  enemy = inputs.entity;
  return new $behavior_tree.BehaviorResult(
    (enemy.last_path_updated + 1000) < $utils.now_in_milliseconds(),
    enemy,
    new AdditionalOutputs(toList([])),
  );
}

export function random_path_behavior(inputs) {
  let enemy;
  let inputs$1;
  enemy = inputs.entity;
  inputs$1 = inputs.additional_inputs;
  let path = toList([
    (() => {
      let _pipe = enemy.position;
      return random_direction(_pipe, inputs$1.dungeon);
    })(),
  ]);
  let enemy$1 = new Enemy(
    enemy.position,
    enemy.rotation,
    enemy.velocity,
    enemy.rotational_velocity,
    enemy.current_health,
    enemy.max_health,
    path,
    $utils.now_in_milliseconds(),
    enemy.spotted_player,
    enemy.last_bullet_fired,
    enemy.btree,
  );
  return new $behavior_tree.BehaviorResult(
    true,
    enemy$1,
    new AdditionalOutputs(toList([])),
  );
}

export function in_range_of_player_behavior(inputs) {
  let enemy;
  let dungeon;
  let player;
  enemy = inputs.entity;
  dungeon = inputs.additional_inputs.dungeon;
  player = inputs.additional_inputs.player;
  return $bool.guard(
    $vector.distance(enemy.position, player.position) > $int.to_float(
      globalThis.Math.trunc($dungeon.room_size / 2),
    ),
    new $behavior_tree.BehaviorResult(
      false,
      enemy,
      new AdditionalOutputs(toList([])),
    ),
    () => {
      return new $behavior_tree.BehaviorResult(
        $dungeon.can_move(dungeon, enemy.position, player.position),
        enemy,
        new AdditionalOutputs(toList([])),
      );
    },
  );
}

export function spot_player_behavior(inputs) {
  let enemy;
  enemy = inputs.entity;
  return new $behavior_tree.BehaviorResult(
    true,
    new Enemy(
      enemy.position,
      enemy.rotation,
      enemy.velocity,
      enemy.rotational_velocity,
      enemy.current_health,
      enemy.max_health,
      enemy.path,
      enemy.last_path_updated,
      true,
      enemy.last_bullet_fired,
      enemy.btree,
    ),
    new AdditionalOutputs(toList([])),
  );
}

export function spotted_player_behavior(inputs) {
  let enemy;
  enemy = inputs.entity;
  return new $behavior_tree.BehaviorResult(
    enemy.spotted_player,
    enemy,
    new AdditionalOutputs(toList([])),
  );
}

export function path_to_player_behavior(inputs) {
  let enemy;
  let dungeon;
  let player;
  enemy = inputs.entity;
  dungeon = inputs.additional_inputs.dungeon;
  player = inputs.additional_inputs.player;
  let path = $dungeon.path_to(dungeon, enemy.position, player.position);
  let _block;
  let _pipe = path;
  _block = $list.take(_pipe, $list.length(path) - 1);
  let path$1 = _block;
  let enemy$1 = new Enemy(
    enemy.position,
    enemy.rotation,
    enemy.velocity,
    enemy.rotational_velocity,
    enemy.current_health,
    enemy.max_health,
    path$1,
    $utils.now_in_milliseconds(),
    enemy.spotted_player,
    enemy.last_bullet_fired,
    enemy.btree,
  );
  return new $behavior_tree.BehaviorResult(
    true,
    enemy$1,
    new AdditionalOutputs(toList([])),
  );
}

export function in_air_behavior(inputs) {
  let enemy;
  enemy = inputs.entity;
  return new $behavior_tree.BehaviorResult(
    enemy.position.z > 0.0,
    enemy,
    new AdditionalOutputs(toList([])),
  );
}

export function move_in_air_behavior(inputs) {
  let enemy;
  let dungeon;
  enemy = inputs.entity;
  dungeon = inputs.additional_inputs.dungeon;
  let _block;
  let _pipe = enemy.position;
  _block = $vector.add(_pipe, enemy.velocity);
  let new_position = _block;
  let _block$1;
  let $ = $dungeon.can_move(dungeon, enemy.position, new_position);
  if ($) {
    _block$1 = new_position;
  } else {
    _block$1 = new $vector.Vector(
      enemy.position.x,
      enemy.position.y,
      enemy.position.z + enemy.velocity.z,
    );
  }
  let new_position$1 = _block$1;
  return new $behavior_tree.BehaviorResult(
    true,
    new Enemy(
      new_position$1,
      enemy.rotation,
      enemy.velocity,
      enemy.rotational_velocity,
      enemy.current_health,
      enemy.max_health,
      enemy.path,
      enemy.last_path_updated,
      enemy.spotted_player,
      enemy.last_bullet_fired,
      enemy.btree,
    ),
    new AdditionalOutputs(toList([])),
  );
}

export function is_facing_player_behavior(inputs) {
  let enemy;
  let player;
  enemy = inputs.entity;
  player = inputs.additional_inputs.player;
  let player2d = $vector.vector_2d(player.position);
  let enemy2d = $vector.vector_2d(enemy.position);
  let target = $vector.subtract(player2d, enemy2d);
  let target$1 = $vector.multiply(
    target,
    divideFloat(
      $vector.dot($vector.from_angle2d(enemy.rotation), target),
      $vector.magnitude(target)
    ),
  );
  let target$2 = $vector.add(target$1, enemy2d);
  return new $behavior_tree.BehaviorResult(
    $vector.distance(target$2, player2d) < ($player.player_size / 2.0),
    enemy,
    new AdditionalOutputs(toList([])),
  );
}

/**
 * Applies damage to the enemy.
 */
export function apply_damage(enemy, damage) {
  return new Enemy(
    enemy.position,
    enemy.rotation,
    enemy.velocity,
    enemy.rotational_velocity,
    enemy.current_health - damage,
    enemy.max_health,
    enemy.path,
    enemy.last_path_updated,
    enemy.spotted_player,
    enemy.last_bullet_fired,
    enemy.btree,
  );
}

/**
 * Is the enemy currently dead.
 */
export function is_enemy_dead(enemy) {
  return enemy.current_health <= 0;
}

function connection_crosses(start, end, center, radius) {
  let diff = $vector.subtract(end, start);
  let t = (divideFloat(
    ((((-1.0 * diff.x) * (start.x - center.x)) - (diff.y * (start.y - center.y))) - (diff.z * (start.z - center.z))),
    $vector.dot(diff, diff)
  ));
  let $ = (t > 1.0) || (t < 0.0);
  if ($) {
    let z = $vector.distance(
      $vector.add(start, $vector.multiply(diff, 0.0)),
      center,
    );
    let o = $vector.distance(
      $vector.add(start, $vector.multiply(diff, 1.0)),
      center,
    );
    return (z < radius) || (o < radius);
  } else {
    return $vector.distance(
      $vector.add(start, $vector.multiply(diff, t)),
      center,
    ) < radius;
  }
}

function clamp_radians(loop$input_radians) {
  while (true) {
    let input_radians = loop$input_radians;
    let pi = $maths.pi();
    let _block;
    let $ = input_radians < (-1.0 * pi);
    if ($) {
      _block = clamp_radians(input_radians + (pi * 2.0));
    } else {
      _block = input_radians;
    }
    let input_radians$1 = _block;
    let $1 = input_radians$1 > pi;
    if ($1) {
      loop$input_radians = input_radians$1 - (pi * 2.0);
    } else {
      return input_radians$1;
    }
  }
}

function target_rotation_range() {
  return $maths.pi() / 30.0;
}

function slow_rotation_range() {
  return $maths.pi() / 15.0;
}

function max_angular_speed() {
  return $maths.pi() / 20.0;
}

function max_angular_acceleration() {
  return $maths.pi() / 40.0;
}

/**
 * Checks if the object at the given position with given radius collides with the enemy.
 */
export function collides_with(enemy, position, size) {
  return $vector.distance(enemy.position, position) < ((size / 2.0) + (enemy_size / 2.0));
}

export function fire_bullet_behavior(inputs) {
  let enemy;
  enemy = inputs.entity;
  let now = $utils.now_in_milliseconds();
  let $ = (enemy.last_bullet_fired + bullet_wait_time) > now;
  if ($) {
    return new $behavior_tree.BehaviorResult(
      false,
      enemy,
      new AdditionalOutputs(toList([])),
    );
  } else {
    return new $behavior_tree.BehaviorResult(
      true,
      new Enemy(
        enemy.position,
        enemy.rotation,
        enemy.velocity,
        enemy.rotational_velocity,
        enemy.current_health,
        enemy.max_health,
        enemy.path,
        enemy.last_path_updated,
        enemy.spotted_player,
        now,
        enemy.btree,
      ),
      new AdditionalOutputs(
        toList([
          $bullet.spawn_bullet(
            enemy.position,
            $vector.from_angle2d(enemy.rotation),
            false,
          ),
        ]),
      ),
    );
  }
}

/**
 * Applies gravity to the velocity and resets z position to floor when appropriate.
 */
export function apply_gravity(enemy) {
  let _block;
  let $ = enemy.position.z;
  let z = $;
  if (z < 0.0) {
    _block = new $vector.Vector(enemy.position.x, enemy.position.y, 0.0);
  } else {
    _block = enemy.position;
  }
  let position = _block;
  return new Enemy(
    position,
    enemy.rotation,
    new $vector.Vector(
      enemy.velocity.x,
      enemy.velocity.y,
      enemy.velocity.z - enemy_gravity_strength,
    ),
    enemy.rotational_velocity,
    enemy.current_health,
    enemy.max_health,
    enemy.path,
    enemy.last_path_updated,
    enemy.spotted_player,
    enemy.last_bullet_fired,
    enemy.btree,
  );
}

function create_whisker_points(enemy) {
  let _block;
  let _pipe = enemy.velocity;
  let _pipe$1 = $vector.normalize(_pipe);
  _block = $vector.multiply(_pipe$1, whisker_length);
  let whisker_dist = _block;
  let pi_div_4 = $maths.pi() / 4.0;
  return toList([
    (() => {
      let _pipe$2 = whisker_dist;
      let _pipe$3 = $vector.rotate2d(_pipe$2, pi_div_4);
      return $vector.add(_pipe$3, enemy.position);
    })(),
    (() => {
      let _pipe$2 = whisker_dist;
      let _pipe$3 = $vector.rotate2d(_pipe$2, pi_div_4 * -1.0);
      return $vector.add(_pipe$3, enemy.position);
    })(),
  ]);
}

function steer_enemy(enemy, target) {
  let dir = $vector.subtract(target, enemy.position);
  let _block;
  let _pipe = dir;
  _block = $vector.magnitude(_pipe);
  let dist = _block;
  let _block$1;
  let _block$2;
  let $ = dist < target_radius;
  if ($) {
    _block$2 = dir;
  } else {
    _block$2 = enemy.velocity;
  }
  let _pipe$1 = _block$2;
  _block$1 = $vector.heading2d(_pipe$1);
  let target_rotation = _block$1;
  let rotation_diff = clamp_radians(target_rotation - enemy.rotation);
  let rotation_mag = $float.absolute_value(rotation_diff);
  return $bool.guard(
    rotation_mag < target_rotation_range(),
    enemy,
    () => {
      let target_rotation_velocity = divideFloat(
        ((() => {
          let $1 = rotation_mag > slow_rotation_range();
          if ($1) {
            return max_angular_speed();
          } else {
            return divideFloat(
              (max_angular_speed() * rotation_mag),
              slow_rotation_range()
            );
          }
        })() * rotation_diff),
        rotation_mag
      );
      let rotational_acceleration = target_rotation_velocity - enemy.rotational_velocity;
      let rotational_acc_mag = $float.absolute_value(rotational_acceleration);
      let _block$3;
      let $1 = rotational_acc_mag > max_angular_acceleration();
      if ($1) {
        _block$3 = divideFloat(
          (rotational_acceleration * max_angular_acceleration()),
          rotational_acc_mag
        );
      } else {
        _block$3 = rotational_acceleration;
      }
      let rotational_acceleration$1 = _block$3;
      let rotational_velocity = enemy.rotational_velocity + rotational_acceleration$1;
      return new Enemy(
        enemy.position,
        enemy.rotation + rotational_velocity,
        enemy.velocity,
        rotational_velocity,
        enemy.current_health,
        enemy.max_health,
        enemy.path,
        enemy.last_path_updated,
        enemy.spotted_player,
        enemy.last_bullet_fired,
        enemy.btree,
      );
    },
  );
}

function avoid_thing_at_position(enemy, position, acceleration, force, radius) {
  let dir = $vector.subtract(enemy.position, position);
  let dist = $vector.magnitude_squared(dir);
  let $ = dist < (radius * radius);
  if ($) {
    let repulsion = $float.min(divideFloat(force, dist), max_acceleration);
    let _pipe = dir;
    let _pipe$1 = $vector.normalize(_pipe);
    let _pipe$2 = $vector.multiply(_pipe$1, repulsion);
    return $vector.add(_pipe$2, acceleration);
  } else {
    return acceleration;
  }
}

function landing_position_if_jumped(position, velocity) {
  let time_in_air = divideFloat(jump_power, enemy_gravity_strength);
  return $vector.add(position, $vector.multiply(velocity, time_in_air));
}

function move_enemy(enemy, target, obstacles, pits, enemies, whisker_points) {
  let dir = $vector.subtract(target, enemy.position);
  let _block;
  let _pipe = dir;
  _block = $vector.magnitude(_pipe);
  let dist = _block;
  return $bool.guard(
    dist < target_radius,
    [enemy, enemy.position],
    () => {
      let _block$1;
      let $ = dist > slow_radius;
      if ($) {
        _block$1 = max_speed;
      } else {
        _block$1 = divideFloat((max_speed * dist), slow_radius);
      }
      let target_speed = _block$1;
      let _block$2;
      let _pipe$1 = dir;
      let _pipe$2 = $vector.normalize(_pipe$1);
      _block$2 = $vector.multiply(_pipe$2, target_speed);
      let target_velocity = _block$2;
      let acceleration = $vector.subtract(target_velocity, enemy.velocity);
      let $1 = $list.fold(
        pits,
        [acceleration, false],
        (acc, p) => {
          let is_jumping = acc[1];
          let dir$1 = $vector.subtract(enemy.position, p.position);
          let dist$1 = $vector.magnitude(dir$1);
          let $2 = (dist$1 < pit_avoid_radius) && !is_jumping;
          if ($2) {
            let landing_position = landing_position_if_jumped(
              enemy.position,
              enemy.velocity,
            );
            let $3 = connection_crosses(
              enemy.position,
              landing_position,
              p.position,
              p.size / 2.0,
            ) && ($vector.distance(landing_position, p.position) > (p.size / 2.0));
            if ($3) {
              return [
                new $vector.Vector(
                  0.0,
                  0.0,
                  (-1.0 * enemy.velocity.z) + jump_power,
                ),
                true,
              ];
            } else {
              return [
                avoid_thing_at_position(
                  enemy,
                  p.position,
                  acc[0],
                  pit_avoid_force,
                  pit_avoid_radius,
                ),
                false,
              ];
            }
          } else {
            return acc;
          }
        },
      );
      let acceleration$1;
      let is_jumping;
      acceleration$1 = $1[0];
      is_jumping = $1[1];
      let acceleration$2 = $bool.guard(
        is_jumping,
        acceleration$1,
        () => {
          let _pipe$3 = acceleration$1;
          let _pipe$4 = ((_capture) => {
            return $list.fold(
              obstacles,
              _capture,
              (acc, o) => {
                return avoid_thing_at_position(
                  enemy,
                  o.position,
                  acc,
                  avoid_force,
                  avoid_radius,
                );
              },
            );
          })(_pipe$3);
          let _pipe$5 = ((_capture) => {
            return $list.fold(
              enemies,
              _capture,
              (acc, e) => {
                let $2 = isEqual(e.position, enemy.position);
                if ($2) {
                  return acc;
                } else {
                  return avoid_thing_at_position(
                    enemy,
                    e.position,
                    acc,
                    cluster_force,
                    cluster_radius,
                  );
                }
              },
            );
          })(_pipe$4);
          let _pipe$6 = $vector.limit(_pipe$5, max_acceleration);
          let _pipe$7 = ((_capture) => {
            return $list.fold(
              whisker_points,
              _capture,
              (acc, w) => {
                let _block$3;
                if (w instanceof $room.Left) {
                  _block$3 = new $vector.Vector(
                    -1.0 * wall_avoid_force,
                    0.0,
                    0.0,
                  );
                } else if (w instanceof $room.Right) {
                  _block$3 = new $vector.Vector(wall_avoid_force, 0.0, 0.0);
                } else if (w instanceof $room.Top) {
                  _block$3 = new $vector.Vector(
                    0.0,
                    -1.0 * wall_avoid_force,
                    0.0,
                  );
                } else if (w instanceof $room.Bottom) {
                  _block$3 = new $vector.Vector(0.0, wall_avoid_force, 0.0);
                } else if (w instanceof $room.TopLeft) {
                  _block$3 = new $vector.Vector(
                    -1.0 * wall_avoid_force,
                    -1.0 * wall_avoid_force,
                    0.0,
                  );
                } else if (w instanceof $room.TopRight) {
                  _block$3 = new $vector.Vector(
                    wall_avoid_force,
                    -1.0 * wall_avoid_force,
                    0.0,
                  );
                } else if (w instanceof $room.BottomLeft) {
                  _block$3 = new $vector.Vector(
                    -1.0 * wall_avoid_force,
                    wall_avoid_force,
                    0.0,
                  );
                } else {
                  _block$3 = new $vector.Vector(
                    wall_avoid_force,
                    wall_avoid_force,
                    0.0,
                  );
                }
                let add = _block$3;
                return $vector.add(add, acc);
              },
            );
          })(_pipe$6);
          return $vector.limit(_pipe$7, max_acceleration);
        },
      );
      let velocity = $vector.add(enemy.velocity, acceleration$2);
      return [
        new Enemy(
          enemy.position,
          enemy.rotation,
          velocity,
          enemy.rotational_velocity,
          enemy.current_health,
          enemy.max_health,
          enemy.path,
          enemy.last_path_updated,
          enemy.spotted_player,
          enemy.last_bullet_fired,
          enemy.btree,
        ),
        $vector.add(velocity, enemy.position),
      ];
    },
  );
}

export function follow_path_behavior(inputs) {
  let enemy;
  let inputs$1;
  enemy = inputs.entity;
  inputs$1 = inputs.additional_inputs;
  let enemies;
  let dungeon;
  enemies = inputs$1.enemies;
  dungeon = inputs$1.dungeon;
  let $ = enemy.path;
  if ($ instanceof $Empty) {
    return new $behavior_tree.BehaviorResult(
      false,
      enemy,
      new AdditionalOutputs(toList([])),
    );
  } else {
    let next = $.head;
    let rest = $.tail;
    let $1 = $vector.distance(next, enemy.position) < ($int.to_float(
      $dungeon.room_size,
    ) / 10.0);
    if ($1) {
      return new $behavior_tree.BehaviorResult(
        true,
        new Enemy(
          enemy.position,
          enemy.rotation,
          enemy.velocity,
          enemy.rotational_velocity,
          enemy.current_health,
          enemy.max_health,
          rest,
          enemy.last_path_updated,
          enemy.spotted_player,
          enemy.last_bullet_fired,
          enemy.btree,
        ),
        new AdditionalOutputs(toList([])),
      );
    } else {
      let _block;
      let _pipe = create_whisker_points(enemy);
      _block = $list.filter_map(
        _pipe,
        (_capture) => {
          return $dungeon.get_reflecting_point(
            dungeon,
            enemy.position,
            _capture,
          );
        },
      );
      let whiskers = _block;
      let $2 = move_enemy(
        enemy,
        next,
        dungeon.obstacles,
        dungeon.pits,
        enemies,
        whiskers,
      );
      let enemy$1;
      let new_position;
      enemy$1 = $2[0];
      new_position = $2[1];
      let _block$1;
      let $3 = $dungeon.can_move(dungeon, enemy$1.position, new_position);
      if ($3) {
        _block$1 = new_position;
      } else {
        _block$1 = new $vector.Vector(
          enemy$1.position.x,
          enemy$1.position.y,
          enemy$1.position.z + enemy$1.velocity.z,
        );
      }
      let new_position$1 = _block$1;
      let _block$2;
      let _pipe$1 = new Enemy(
        new_position$1,
        enemy$1.rotation,
        enemy$1.velocity,
        enemy$1.rotational_velocity,
        enemy$1.current_health,
        enemy$1.max_health,
        enemy$1.path,
        enemy$1.last_path_updated,
        enemy$1.spotted_player,
        enemy$1.last_bullet_fired,
        enemy$1.btree,
      );
      _block$2 = steer_enemy(_pipe$1, next);
      let enemy$2 = _block$2;
      return new $behavior_tree.BehaviorResult(
        true,
        enemy$2,
        new AdditionalOutputs(toList([])),
      );
    }
  }
}

export function follow_player_behavior(inputs) {
  let enemy;
  let enemies;
  let dungeon;
  let player;
  enemy = inputs.entity;
  enemies = inputs.additional_inputs.enemies;
  dungeon = inputs.additional_inputs.dungeon;
  player = inputs.additional_inputs.player;
  let target = $vector.vector_2d(player.position);
  let _block;
  let _pipe = create_whisker_points(enemy);
  _block = $list.filter_map(
    _pipe,
    (_capture) => {
      return $dungeon.get_reflecting_point(dungeon, enemy.position, _capture);
    },
  );
  let whiskers = _block;
  let $ = move_enemy(
    enemy,
    target,
    dungeon.obstacles,
    dungeon.pits,
    enemies,
    whiskers,
  );
  let enemy$1;
  let new_position;
  enemy$1 = $[0];
  new_position = $[1];
  let _block$1;
  let $1 = $dungeon.can_move(dungeon, enemy$1.position, new_position);
  if ($1) {
    _block$1 = new_position;
  } else {
    _block$1 = new $vector.Vector(
      enemy$1.position.x,
      enemy$1.position.y,
      enemy$1.position.z + enemy$1.velocity.z,
    );
  }
  let new_position$1 = _block$1;
  let _block$2;
  let _pipe$1 = new Enemy(
    new_position$1,
    enemy$1.rotation,
    enemy$1.velocity,
    enemy$1.rotational_velocity,
    enemy$1.current_health,
    enemy$1.max_health,
    enemy$1.path,
    enemy$1.last_path_updated,
    enemy$1.spotted_player,
    enemy$1.last_bullet_fired,
    enemy$1.btree,
  );
  _block$2 = steer_enemy(_pipe$1, target);
  let enemy$2 = _block$2;
  return new $behavior_tree.BehaviorResult(
    true,
    enemy$2,
    new AdditionalOutputs(toList([])),
  );
}

/**
 * Makes a new enemy.
 */
export function new_enemy(initial_position) {
  let default_output = new AdditionalOutputs(toList([]));
  let output_to_input = (i, _) => { return i; };
  let output_merge = (old, new$) => {
    return new AdditionalOutputs($list.append(old.bullets, new$.bullets));
  };
  let sequence = (_capture) => {
    return $behavior_tree.sequence(
      _capture,
      default_output,
      output_to_input,
      output_merge,
    );
  };
  let selector = (_capture) => {
    return $behavior_tree.selector(
      _capture,
      default_output,
      output_to_input,
      output_merge,
    );
  };
  return new Enemy(
    initial_position,
    0.0,
    new $vector.Vector(0.0, 0.0, 0.0),
    0.0,
    max_enemy_health,
    max_enemy_health,
    toList([]),
    0,
    false,
    0,
    $behavior_tree.all(
      toList([
        sequence(toList([in_range_of_player_behavior, spot_player_behavior])),
        selector(
          toList([
            sequence(toList([spotted_player_behavior, path_to_player_behavior])),
            sequence(
              toList([
                selector(
                  toList([
                    $behavior_tree.not(has_path_behavior),
                    path_is_stale_behavior,
                  ]),
                ),
                random_path_behavior,
              ]),
            ),
          ]),
        ),
        selector(
          toList([
            sequence(toList([in_air_behavior, move_in_air_behavior])),
            sequence(toList([has_path_behavior, follow_path_behavior])),
            sequence(toList([spotted_player_behavior, follow_player_behavior])),
          ]),
        ),
        sequence(
          toList([
            in_range_of_player_behavior,
            is_facing_player_behavior,
            fire_bullet_behavior,
          ]),
        ),
      ]),
      default_output,
      output_to_input,
      output_merge,
    ),
  );
}

/**
 * Renders the player to the screen.
 */
export function draw(p, enemy) {
  let $ = is_enemy_dead(enemy);
  if ($) {
    $p5.fill(p, dead_fill_color)
  } else {
    $p5.fill(p, enemy_fill_color)
  }
  let $1 = $float.power(enemy_size, 1.0 + (enemy.position.z / 4.0));
  let size_to_draw;
  if ($1 instanceof Ok) {
    size_to_draw = $1[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "enemy",
      811,
      "draw",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $1,
        start: 23633,
        end: 23722,
        pattern_start: 23644,
        pattern_end: 23660
      }
    )
  }
  let size_to_draw$1 = $float.max(size_to_draw, enemy_size / 2.0);
  let _pipe = p;
  let _pipe$1 = $p5.stroke(_pipe, enemy_stroke_color);
  let _pipe$2 = $p5.circle(
    _pipe$1,
    enemy.position.x,
    enemy.position.y,
    size_to_draw$1,
  );
  return $p5.line(
    _pipe$2,
    enemy.position.x,
    enemy.position.y,
    enemy.position.x + ((($maths.cos(enemy.rotation) * size_to_draw$1)) / 2.0),
    enemy.position.y + ((($maths.sin(enemy.rotation) * size_to_draw$1)) / 2.0),
  );
}
